import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import MultiPose from '../../PackageBuilders/MultiPose';
import { useCustomer } from '../../../contexts/CustomerContext';
import FavoritePoseSelection from '../_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';
import useBackgrounds from '../../../hooks/useBackgrounds';
import usePoses from '../../../hooks/usePoses';
import usePreorderPose from '../../../hooks/usePreorderPose';
import useQueryString from '../../../hooks/use-query-string';
import routes from '../../../routes';
import { OrderPackage } from '../../../models';
import OptionSelectionContent from '../OptionSelection/OptionSelectionContent';
import usePricelist from '../../../hooks/usePricelist';
import useRetouchingExtra from '../../../hooks/useRetouchingExtra';
import useAddons from '../../../hooks/useAddons';
import QueryKeys from '../../../utils/QueryKeys';
import SinglePoseMutator from './SinglePoseMutator';
import useImageTypes from '../../../hooks/useImageTypes';
import useMultiSeasonPoses from "../../../hooks/useMultiSeasonPoses";
import useHolidayPricelist from "../../../hooks/useHolidayPricelist";

export default function MultiPosePackageBuilder() {
  // Routing and queries
  const { packageId } = useParams();
  const history = useHistory();
  const {
    step,
    opt: optionId,
    pastImages,
    favorites,
    preorderPoseId,
    type,
  } = useQueryString();
  const favoriteIds = [favorites].flat().filter((id) => !!id).map((id) => id.toString());

  // Data
  const { customer, setCustomer } = useCustomer();
  const [orderPackage] = useState(new OrderPackage());
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: pricelist } = usePricelist(packageId);
  const { data: poses } = pricelist?.id && (pricelist?.multipleSeasons?.length || pricelist?.displaySharedPoses) ? (
      useMultiSeasonPoses(customer.currentStudent.id, packageId)
    ) : (
    usePoses(customer.currentStudent.id, packageId, pastImages)
  )

  const { data: preorderPose } = usePreorderPose(preorderPoseId)

  useRetouchingExtra();
  const { data: addons } = useAddons(true);

  const { data: imageTypes } = useImageTypes(optionId);

  const favoritePoses = favoriteIds.map((id) => (poses || []).find((pose) => pose.id === id));

  // UI
  const isFetchingPoses = useIsFetching({ queryKey: [QueryKeys.STUDENT_IMAGES] });
  const isFetchingBackgrounds = useIsFetching({ queryKey: [QueryKeys.BACKGROUNDS] });
  const isFetchingPricelist = useIsFetching({ queryKey: [QueryKeys.PRICELIST] });
  const { data: giftPricelist } = useHolidayPricelist(customer.currentSchool?.id);

  const isFetching = isFetchingPoses || isFetchingBackgrounds || isFetchingPricelist;

  const onSave = () => {
    setCustomer({ ...customer, currentOrderPackage: orderPackage });

    if (giftPricelist) history.push(routes.holiday.builder({ optional: true, o: orderPackage.id, pid: orderPackage.packageId }))
    else if (addons.length === 0) history.push(routes.retouching(orderPackage.id));
    else history.push(routes.addons(orderPackage.id));
  };

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  if (!(packageId && customer.currentSchool && customer.currentStudent)) return null;
  if (isFetching) return null;
  if (backgrounds.length === 0) return null;
  if (poses?.length === 0 && !preorderPose) return null;

  const background = backgrounds[0];

  const isAllJpg = (poses || []).reduce((acc, cur) => acc && !cur.extension.toLowerCase().includes('png'), true);

  return (
    <div>
      {(!step || step === 'pose-selection') && (
        <FavoritePoseSelection
          backgrounds={backgrounds}
          background={background}
          images={poses}
        />
      )}
      {step === 'select-package' && pricelist && (
        <OptionSelectionContent
          orderPackage={orderPackage}
          foreground={favoritePoses.length > 0 ? favoritePoses[0].watermarkUrl : (preorderPose || poses[0]).watermarkUrl}
          background={background}
          multiPoseHandler={(selectedOpt) => history.push(routes.elementaryMultiPoseBuilder(packageId, { favorites: favoriteIds, preorderPoseId: preorderPoseId, opt: selectedOpt.id, step: 'build' }))}
          packageId={packageId}
          accessCodeOnly={false}
          preorderPose={preorderPose}
        />
      )}
      {step === 'build' && poses.length === 1 && isAllJpg && (
        <SinglePoseMutator
          cartId={customer.cart.id}
          onSave={onSave}
          imageTypes={imageTypes}
          orderPackage={orderPackage}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          schoolId={customer.currentSchool.id}
          packageId={packageId}
          poses={poses}
        />
      )}
      {step === 'build' && (poses.length > 1 || !isAllJpg || preorderPose) && (
        <MultiPose
          cartId={customer.cart.id}
          packageId={packageId}
          schoolId={customer.currentSchool.id}
          studentId={customer.currentStudent.id}
          optionId={optionId}
          onSave={onSave}
          favoriteIds={favoriteIds}
          // This is the favorites-picker flow (URL `favorites=`), so restrict the builder
          // to the chosen poses. Other MultiPose callers (senior, edit) leave this false
          // and keep the full session -- their favoriteIds only sort/highlight.
          restrictToFavorites
          orderPackage={orderPackage}
          pastImages={pastImages}
          preorderPose={preorderPose}
        />
      )}
    </div>
  );
}
